import React, { useContext } from 'react';
import classNames from 'classnames';
import { ScrollSyncPane } from './ScrollSync';
import { If } from '@/components/Utils/If';
import { MaterialProgressBar } from '@/components/MaterialProgressBar';
import TableContext from './TableContext';

function TableHeaderCell({ column, index }: { column: any; index: number }) {
  const {
    table: { getToggleAllRowsExpandedProps, isAllRowsExpanded },
    props: { expandable, expandToggleColumn },
  } = useContext(TableContext);

  const { key, ...headerProps } = column.getHeaderProps({
    className: classNames(column.className || '', 'th', {
      [`align-${column.align}`]: column.align,
    }),
  });

  return (
    <div key={key} {...headerProps}>
      <If condition={expandable && index + 1 === expandToggleColumn}>
        <span {...getToggleAllRowsExpandedProps()} className="expand-toggle">
          <span
            className={classNames({
              'arrow-down': isAllRowsExpanded,
              'arrow-right': !isAllRowsExpanded,
            })}
          />
        </span>
      </If>

      <div
        {...column.getSortByToggleProps({
          className: classNames('cell-inner', {
            'text-overview': column.textOverview,
          }),
        })}
      >
        {column.render('Header')}

        <If condition={column.isSorted}>
          <span
            className={classNames(
              {
                'sort-icon--desc': column.isSortedDesc,
                'sort-icon--asc': !column.isSortedDesc,
              },
              'sort-icon',
            )}
          ></span>
        </If>
      </div>

      {column.canResize && (
        <div
          {...column.getResizerProps()}
          className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
        >
          <div className="inner-resizer" />
        </div>
      )}
    </div>
  );
}

function TableHeaderGroup({ headerGroup }: { headerGroup: any }) {
  const { key, ...groupProps } = headerGroup.getHeaderGroupProps();
  return (
    <div key={key} {...groupProps} className="tr">
      {headerGroup.headers.map((column: any, index: number) => (
        <TableHeaderCell key={index} column={column} index={index} />
      ))}
    </div>
  );
}

/**
 * Table header.
 */
export default function TableHeader() {
  const {
    table: { headerGroups, page },
    props: {
      TableHeaderSkeletonRenderer,
      headerLoading,
      progressBarLoading,
      hideTableHeader,
    },
  } = useContext(TableContext);

  // Can't contiunue if the thead is disabled.
  if (hideTableHeader) {
    return null;
  }

  if (headerLoading && TableHeaderSkeletonRenderer) {
    return <TableHeaderSkeletonRenderer />;
  }
  return (
    <ScrollSyncPane>
      <div className="thead">
        <div className={'thead-inner'}>
          {headerGroups.map((headerGroup: any, index: number) => (
            <TableHeaderGroup key={index} headerGroup={headerGroup} />
          ))}
          <If condition={progressBarLoading}>
            <MaterialProgressBar />
          </If>
        </div>
      </div>
    </ScrollSyncPane>
  );
}
